import { Button, Heading, Input, Text, Stack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as zod from 'zod';

import { SignedOutFormContainer } from '../../../components/SignedOutFormContainer';
import { SignedOutHeader } from '../../../components/SignedOutHeader';
import { SignedOutWrapper } from '../../../components/SignedOutWrapper';
import {
  SignedOutNavigationScreenNavigationProp,
  SignedOutRootStackParamList,
} from '../../../routes/types';
import { useSessionActions } from '../../../store/session/actions';

const validationSchema = zod.object({
  email: zod
    .string({ required_error: 'O campo e-mail é obrigatório' })
    .min(1, 'O campo e-mail é obrigatório')
    .email({ message: 'E-mail inválido' }),
});

type RecoverPasswordFormData = zod.infer<typeof validationSchema>;
export const ForgotPasswordRequest: React.FC = () => {
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const navigation = useNavigation<SignedOutNavigationScreenNavigationProp>();
  const { params } = useRoute<RouteProp<SignedOutRootStackParamList, 'ForgotPasswordRequest'>>();
  const { recoverPasswordRequest } = useSessionActions();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RecoverPasswordFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      email: params?.emailOrDocument || '',
    },
  });

  const handleRecoverPassword = (data: RecoverPasswordFormData) => {
    setIsRecoveringPassword(true);
    recoverPasswordRequest(data.email)
      .then(() => {
        setTimeout(() => {
          navigation.navigate({ key: 'SignIn' });
        }, 1000);
      })
      .finally(() => {
        setIsRecoveringPassword(false);
      });
  };

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      return navigation.goBack();
    }

    navigation.navigate({ key: 'SignIn' });
  }, [navigation]);

  return (
    <SignedOutWrapper>
      <SignedOutHeader isMobile={isMobile} />
      <SignedOutFormContainer>
        <Heading
          testId="singin-title"
          size="h1"
          fontSize={{ base: Typography['fontsize.lg'], sm: Typography['fontsize.xxl'] }}
          variant="semibold">
          Redefinição de senha
        </Heading>
        <Text color="neutral.dark">
          Um link para redefinição de senha será enviado para o seu e-mail cadastrado.
        </Text>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            E-mail
          </Text>
          <Input
            control={control}
            placeholder="Digite seu e-mail"
            keyboardType="email-address"
            name="email"
            autoComplete="email"
            variant="filled"
            error={errors.email}
            isDisabled={isRecoveringPassword}
            hasShadow={false}
          />
        </VStack>
        <Text color="neutral.dark">
          Caso o e-mail esteja errado, envie uma mensagem para middleb2b@genialinvestimentos.com.br
        </Text>
        <Stack
          direction={{ base: 'column', lg: 'row-reverse' }}
          style={{ gap: Utils.Token.ToInt(Spacing, 'stack.nano') }}>
          <Button
            onPress={handleSubmit(handleRecoverPassword)}
            disabled={isRecoveringPassword}
            flex={1}>
            Enviar E-mail
          </Button>
          <Button
            onPress={handleGoBack}
            variant="secondary"
            disabled={isRecoveringPassword}
            flex={1}>
            Voltar
          </Button>
        </Stack>
      </SignedOutFormContainer>
    </SignedOutWrapper>
  );
};
